import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught error:', error, errorInfo);
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleClearData = async () => {
    // 致命的エラー時のリカバリー処理:
    // 旧バージョンの localStorage データと、現在の IndexedDB をすべて削除して初期化します。
    try {
      // 1. 旧 localStorage データの削除
      localStorage.removeItem('syukan_habits');
      localStorage.removeItem('syukan_records');
      localStorage.removeItem('syukan_reminders');
      localStorage.removeItem('syukan_settings');
      localStorage.removeItem('hibi_badges');
      localStorage.removeItem('hibi_user_stats');
      
      // 2. 現在の IndexedDB データベースを削除
      if (window.indexedDB) {
        await new Promise((resolve) => {
          const req = indexedDB.deleteDatabase('HabitTrackerDB');
          req.onsuccess = resolve;
          req.onerror = resolve; // エラーでも続行
        });
      }

      sessionStorage.clear();

      // 3. キャッシュの削除
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(cacheNames.map(name => caches.delete(name)));
      }
    } catch (error) {
      console.error('[ErrorBoundary] Recovery cleanup failed:', error);
    }

    // 完全にクリーンな状態でリロード
    window.location.reload();
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: '20px',
          textAlign: 'center',
          fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        }}>
          <h1 style={{ color: '#ff4757', marginBottom: '20px' }}>
            エラーが発生しました
          </h1>
          <p style={{ color: '#666', marginBottom: '30px', maxWidth: '500px' }}>
            アプリケーションの読み込み中にエラーが発生しました。
            以下のボタンをクリックして再読み込みしてください。
          </p>
          
          <div style={{ display: 'flex', gap: '10px', flexDirection: 'column', width: '200px' }}>
            <button
              onClick={this.handleReload}
              style={{
                padding: '12px 24px',
                backgroundColor: '#667eea',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '16px',
                cursor: 'pointer'
              }}
            >
              再読み込み
            </button>
            
            <button
              onClick={this.handleClearData}
              style={{
                padding: '12px 24px',
                backgroundColor: '#ff4757',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              データをクリアして再起動
            </button>
          </div>
          
          {this.state.error && (
            <details style={{ marginTop: '30px', textAlign: 'left', maxWidth: '600px' }}>
              <summary style={{ cursor: 'pointer', color: '#999' }}>
                エラー詳細
              </summary>
              <pre style={{ 
                marginTop: '10px', 
                padding: '10px', 
                backgroundColor: '#f5f5f5',
                borderRadius: '4px',
                fontSize: '12px',
                overflow: 'auto'
              }}>
                {this.state.error.toString()}
                {this.state.error.stack}
              </pre>
            </details>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;